import { useState } from "react";

import { useAddresses, useCreateAddress, useDeleteAddress } from "../api";
import { readApiErrorMessage } from "../errors";
import type { AddressRow } from "../types";

// Phase 4a customer-portal FR-9 (Addresses tab). Add / list /
// delete; one designated default per the existing `default_delivery`
// column (an admin uniqueness guard already enforces "at most one
// default" — we just toggle here).
//
// FR-11 — site-address edits on an active order go through admin,
// not here; this tab manages the customer's profile addresses only.
//
// Phase 4u portal-profile-polish — saved-row cards lifted from a thin
// 1-line border to a proper card with a default-address pill, branded
// remove control, and the NewAddressForm gets the same brand-red Save
// + flag-navy focus ring treatment as ContactTab so the four profile
// tabs read as one design system instead of four.
export function AddressesTab(): JSX.Element {
  const q = useAddresses();
  const create = useCreateAddress();
  const del = useDeleteAddress();
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-brand-red">Couldn&rsquo;t load addresses.</div>;

  function handleDelete(id: number): void {
    setErr(null);
    del.mutate(id, { onError: (e) => setErr(readApiErrorMessage(e)) });
  }

  function handleCreate(input: Pick<AddressRow, "line1" | "line2" | "city" | "state" | "postal_code" | "label">): void {
    setErr(null);
    create.mutate(input, {
      onSuccess: () => setShow(false),
      onError: (e) => setErr(readApiErrorMessage(e))
    });
  }

  return (
    <div className="space-y-4">
      {q.data.rows.length === 0 && !show ? (
        // Phase 4n empty-state polish — audit
        // `.notes/design-grades/portal/03-profile.md` flagged the bare
        // "No addresses saved." line and asked for a sell-the-value
        // empty state. The CTA opens the existing inline form.
        <div
          className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center"
          data-testid="addresses-empty"
        >
          <p className="text-sm font-semibold text-brand-black">No addresses saved yet</p>
          <p className="mt-1 text-sm text-zinc-600">
            Save jobsite addresses to skip data entry next time you book.
          </p>
          <button
            type="button"
            onClick={() => setShow(true)}
            className="mt-4 inline-flex min-h-[44px] items-center rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm transition hover:bg-brand-red-dark focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2"
            data-testid="addresses-empty-cta"
          >
            + Add an address
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {q.data.rows.map((a) => (
            <li
              key={a.id}
              className="flex items-start justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-3 shadow-sm"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-brand-black">
                    {a.label ?? "Address"}
                  </span>
                  {a.default_delivery ? (
                    <span className="rounded-full bg-flag-navy/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-flag-navy">
                      Default
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-zinc-600">
                  {a.line1}
                  {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.postal_code}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(a.id)}
                disabled={del.isPending}
                className="shrink-0 text-xs font-medium text-zinc-500 underline transition hover:text-brand-red disabled:opacity-40"
                data-testid={`delete-address-${a.id}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {err ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 p-3 text-sm text-brand-red"
          data-testid="address-error"
        >
          {err}
        </div>
      ) : null}

      {show ? (
        <NewAddressForm
          onSave={handleCreate}
          onCancel={() => setShow(false)}
          pending={create.isPending}
        />
      ) : q.data.rows.length > 0 ? (
        <button
          type="button"
          onClick={() => setShow(true)}
          className="inline-flex min-h-[44px] items-center rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-brand-black shadow-sm transition hover:border-brand-red/40 hover:text-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2"
          data-testid="add-address"
        >
          + Add address
        </button>
      ) : null}
    </div>
  );
}

interface NewProps {
  onSave: (input: Pick<AddressRow, "line1" | "line2" | "city" | "state" | "postal_code" | "label">) => void;
  onCancel: () => void;
  pending: boolean;
}

// eslint-disable-next-line max-lines-per-function
function NewAddressForm({ onSave, onCancel, pending }: NewProps): JSX.Element {
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [label, setLabel] = useState("");
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-brand-black">Add a new address</p>
      <Input value={label} onChange={setLabel} placeholder="Label (Home / Site / etc.)" testid="addr-label" />
      <Input value={line1} onChange={setLine1} placeholder="Street address" testid="addr-line1" autoComplete="address-line1" />
      <Input value={line2} onChange={setLine2} placeholder="Apt / Suite (optional)" testid="addr-line2" autoComplete="address-line2" />
      <div className="grid gap-2 sm:grid-cols-[1fr_5rem_7rem]">
        <Input value={city} onChange={setCity} placeholder="City" testid="addr-city" autoComplete="address-level2" />
        <Input value={state} onChange={setState} placeholder="ST" testid="addr-state" autoComplete="address-level1" />
        <Input value={postal} onChange={setPostal} placeholder="ZIP" testid="addr-postal" autoComplete="postal-code" />
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave({ line1, line2, city, state, postal_code: postal, label })}
          disabled={pending || line1.trim() === "" || city.trim() === ""}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm transition hover:bg-brand-red-dark focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="addr-save"
        >
          {pending ? "Saving…" : "Save address"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="text-xs font-medium text-zinc-500 underline transition hover:text-brand-red"
          data-testid="addr-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  testid,
  autoComplete
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  testid: string;
  autoComplete?: string;
}): JSX.Element {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="block min-h-[44px] w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-brand-black shadow-sm transition placeholder:text-zinc-400 focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
      data-testid={testid}
    />
  );
}
